"use client";

import { useLingui } from "@lingui/react/macro";
import { invariant } from "@offstreetparking/domain/exceptions";
import type { ConnectionType } from "@workos-inc/node";
import { useEffect, useRef } from "react";
import { getAuthToken } from "~/app/[locale]/auth/actions";
import { isRouterError } from "~/lib/auth";
import { routes } from "~/routes";
import { openProviderLoginPopup, waitForProviderLoginPopup } from "./provider-login-popup";

export const useLoginPopup = ({ onSignedIn }: { onSignedIn?: () => void }) => {
	const { t } = useLingui();
	const waitAbort = useRef<AbortController | null>(null);

	useEffect(() => () => waitAbort.current?.abort(), []);

	const run = async ({
		provider,
		getPopupUrl,
		redirectPage,
	}: {
		provider: `${ConnectionType}` | undefined;
		getPopupUrl: (popupRedirectTo: string) => Promise<string>;
		redirectPage: () => Promise<void>;
	}) => {
		const popup = onSignedIn ? openProviderLoginPopup({ provider }) : null;
		if (!onSignedIn || !popup) {
			try {
				await redirectPage();
			} catch (error) {
				if (!isRouterError(error)) {
					throw error;
				}
			}
			return;
		}

		try {
			popup.window.location.href = await getPopupUrl(routes.authPopupComplete({ popupId: popup.popupId }));
		} catch (error) {
			popup.window.close();
			throw error;
		}

		waitAbort.current = new AbortController();
		const outcome = await waitForProviderLoginPopup({ popup, signal: waitAbort.current.signal });
		invariant(outcome !== "failed", t`Something went wrong. Please try again.`);

		if (outcome === "completed" || (await getAuthToken())) {
			onSignedIn();
		}
	};

	return { run };
};
